import Vue from 'vue';
import VueRouter from 'vue-router';
import designSystemRoutes from 'design-system-routes';
import store from '../store';

// Layouts
import AuthLayout from '../layouts/AuthLayout.vue';
import DashboardLayout from '../layouts/DashboardLayout.vue';

// Pages
import Login from '../views/Login.vue';
import Dashboard from '../views/Dashboard.vue';
import Agents from '../views/Agents.vue';
import AgentDetail from '../views/AgentDetail.vue';
import CallHistory from '../views/CallHistory.vue';
import CallDetail from '../views/CallDetail.vue';
import AgentPage from '../views/AgentPage.vue';
import Signup from '../views/Signup.vue';
import ForgotPassword from '../views/ForgotPassword.vue';
import ResetPassword from '../views/ResetPassword.vue';
import Settings from '../views/Settings.vue';
import ApiTokens from '../views/ApiTokens.vue';
import CreateApiToken from '../views/CreateApiToken.vue';
import TTS from '../views/TTS.vue';
import STT from '../views/STT.vue';
import AgentModels from '../views/AgentModels.vue';
import AgentModelForm from '../views/AgentModelForm.vue';
import AgentConfigs from '../views/AgentConfigs.vue';
import AgentConfigForm from '../views/AgentConfigForm.vue';
import AgentConfigDetail from '../views/AgentConfigDetail.vue';
import SipTrunk from '../views/SipTrunk.vue';

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/login',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'Login',
        component: Login,
        meta: { requiresGuest: true },
      },
    ],
  },
  {
    path: '/signup',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'Signup',
        component: Signup,
        meta: { requiresGuest: true },
      },
    ],
  },
  {
    path: '/forgot-password',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'ForgotPassword',
        component: ForgotPassword,
        meta: { requiresGuest: true },
      },
    ],
  },
  {
    path: '/reset-password',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'ResetPassword',
        component: ResetPassword,
        meta: { requiresGuest: true },
      },
    ],
  },
  {
    path: '/dashboard',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '/agent-models',
        name: 'AgentModels',
        component: AgentModels,
        meta: { requiresSuperuser: true },
      },
      {
        path: '/agent-models/new',
        name: 'CreateAgentModel',
        component: AgentModelForm,
        meta: { requiresSuperuser: true },
      },
      {
        path: '/agent-models/:id',
        name: 'EditAgentModel',
        component: AgentModelForm,
        props: true,
        meta: { requiresSuperuser: true },
      },
      {
        path: '',
        name: 'Dashboard',
        component: Dashboard,
      },
      {
        path: '/agents',
        name: 'Agents',
        component: Agents,
      },
      {
        path: '/agent-config',
        name: 'AgentConfigs',
        component: AgentConfigs,
      },
      {
        path: '/agent-config/create',
        name: 'CreateAgentConfig',
        component: AgentConfigForm,
      },
      {
        path: '/agent-config/:externalId',
        name: 'AgentConfigDetail',
        component: AgentConfigDetail,
        props: true,
      },
      {
        path: '/agent-config/:externalId/edit',
        name: 'EditAgentConfig',
        component: AgentConfigForm,
        props: true,
      },
      {
        path: '/agents/:id',
        name: 'AgentDetail',
        component: AgentDetail,
        props: true,
      },
      {
        path: '/call-history',
        name: 'CallHistory',
        component: CallHistory,
      },
      {
        path: '/call-history/:id',
        name: 'CallDetail',
        component: CallDetail,
      },
      {
        path: '/sip-trunk',
        name: 'SipTrunk',
        component: SipTrunk,
        meta: { requiresSuperuser: true },
      },
      {
        path: '/api-tokens',
        name: 'ApiTokens',
        component: ApiTokens,
      },
      {
        path: '/api-tokens/create',
        name: 'CreateApiToken',
        component: CreateApiToken,
      },
      {
        path: '/tts',
        name: 'TTS',
        component: TTS,
      },
      {
        path: '/stt',
        name: 'STT',
        component: STT,
      },
      {
        path: '/settings',
        name: 'Settings',
        component: Settings,
      },
      ...designSystemRoutes,
      {
        path: '/test-agent/:id',
        name: 'TestAgent',
        component: AgentPage,
      },
      {
        path: '/create-agent',
        name: 'CreateAgent',
        component: AgentPage,
      },
      {
        path: '/edit-agent/:id',
        name: 'EditAgent',
        component: AgentPage,
      },
    ],
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

// Navigation Guards
router.beforeEach((to, from, next) => {
  const isAuthenticated = store.getters['auth/isAuthenticated'];
  const user = store.getters['auth/user'];

  // Authentication gerektiren sayfalar
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!isAuthenticated || !user) {
      next('/login');
      return;
    }
    const needsSuper = to.matched.some((record) => record.meta.requiresSuperuser);
    if (needsSuper && !store.getters['auth/isSuperuser']) {
      store.dispatch('ui/showNotification', {
        type: 'error',
        message: 'This page is only available to super admin users.',
      }, { root: true });
      next('/dashboard');
      return;
    }
    next();
  } else if (to.matched.some((record) => record.meta.requiresGuest)) {
    // Guest kullanıcılar için sayfalar (login sayfası gibi)
    if (isAuthenticated && user) {
      next('/dashboard');
      return;
    }
    next();
  } else {
    // Diğer sayfalar
    next();
  }
});

export default router;
